<template>
  <div id="add-customer-form" class="mt-2">
    <v-form
      v-model="isFormValid"
      ref="customerForm"
      @submit.prevent="submitCustomerForm"
    >
      <card-widget v-if="showCustomerInfo">
        <template #cardBody>
          <!---------------------------- ADD CUSTOMER FORM STARTS ---------------------------->
          <v-row class="pb-4" no-gutters >
            <v-col cols="12" md="12">
              <h6 class="text-h6 font-weight-large secondary--font">
                {{ $dictionary.app.customers.create.customerTitle }}
              </h6>
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col md="12" cols="12" class="pt-0">
              <div class="subtitle-1 mb-6" v-if="isEditCustomer">Customer ID {{customerId}}</div>
              <v-text-field
                v-model="customer.name"
                label="Customer Name"
                class="required"
                outlined
                flat
                :rules="[
                  required('Customer Name'),
                  maxLength(255, 'Customer Name'),
                ]"
            /></v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="customer.address_line_1"
                label="Address Line 1"
                outlined
                :rules="[maxLength(255, 'Address Line 1', false)]"
              />
            </v-col>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="customer.address_line_2"
                label="Address Line 2"
                outlined
                :rules="[maxLength(255, 'Address Line 2', false)]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pt-0">
              <v-autocomplete
                :items="countries"
                v-model="customer.country"
                item-text="label"
                item-value="value"
                label="Country"
                no-data-text="No countries available"
                @change="getCountryStates"
                outlined
              />
            </v-col>
            <v-col md="6" cols="12" class="pt-0">
              <v-autocomplete
                :items="states"
                item-text="label"
                item-value="value"
                v-model="customer.state"
                label="State/Province"
                no-data-text="No state/province available"
                outlined
                :disabled="areStatesLoading"
                :loading="areStatesLoading"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="py-0">
              <v-text-field
                v-model="customer.city"
                label="City"
                outlined
                :rules="[maxLength(255, 'City', false)]"
              />
            </v-col>
            <v-col md="6" cols="12" class="py-0">
              <v-text-field
                v-model="customer.zipcode"
                label="Zip/Postal Code"
                outlined
                max="100"
                :rules="[
                  maxLength(255, 'Zip/Postal Code', false),
                  alphaNumeric('Zip/Postal Code'),
                ]"
              >
              </v-text-field>
            </v-col>
          </v-row>

          <!---------------------------- CUSTOMER FORM ENDS ---------------------------->
        </template>
      </card-widget>

      <card-widget class="mt-7" v-if="showSubscription">
        <template #cardBody>
          <!---------------------------- SUBSCRIPTION INFORMATION FORM START ---------------------------->
          <v-row class="pb-4" no-gutters>
            <v-col cols="12" md="12">
              <h6 class="text-h6 font-weight-large secondary--font">
                {{ $dictionary.app.customers.create.subscriptionTitle }}
              </h6>
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="subscription.subscription_package_type"
                label="Subscription Package"
                outlined
                :rules="[
                  maxLength(255, 'Subscription Package', false),
                ]"
              />
            </v-col>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="subscription.billing_subscription_id"
                label="Biiling Subscription ID"
                outlined
                :rules="[
                  maxLength(255, 'Billing Sucription ID', false),
                  numeric('Billing Sucription ID'),
                ]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pt-0">
              <v-select
                v-model="customer.status"
                label="Billing Status"
                :items="statusOptions"
                class="required"
                outlined
              />
            </v-col>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="subscription.billing_customer_id"
                label="Billing Customer ID"
                outlined
                :rules="[maxLength(255, 'Billing Customer ID', false)]"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col md="6" cols="12" class="pt-0">
              <v-text-field
                v-model="subscription.placement_reports_number"
                label="Allotted Monthly Index Placements"
                outlined
                :rules="[
                  maxLength(255, 'Alloted Monthly Index Placements', false),
                  numeric('Alloted Monthly Index Placements'),
                ]"
              />
            </v-col>
            <v-col md="6" cols="12" class="pt-0">
              <date-range-picker
                :start-date.sync="customer.start_date"
                :end-date.sync="customer.renewal_date"
                end-date-label="Renewal Date"
              ></date-range-picker>
            </v-col>
          </v-row>
          <!---------------------------- SUBSCRIPTION INFORMATION FORM ENDS ---------------------------->
        </template>
      </card-widget>
      <v-divider class="mt-8 mb-6 border-light-grey" />
      <v-row no-gutters class="d-flex justify-start">
        <v-col cols="12">
          <v-btn
            type="submit"
            id="save-customer-settings"
            medium
            color="primary"
            :disabled="!isFormValid"
            class="px-4 button font-weight-bold white-btn-text"
          >
            Save Changes
          </v-btn>
        </v-col>
      </v-row>
    </v-form>
    <loader :loading="isLoading" :size="$appConfig.loader.small" />
  </div>
</template>
<script>
import { CardWidget } from "@/components/widgets";
import { mapGetters, mapActions } from "vuex";
import { isResponseCode, isEmpty } from "@/utils/common.utils";
import { createCustomer, createAccount, updateCustomer } from "@/services";
import DateRangePicker from "@/components/shared/DateRangePicker.vue";
import { 
  RESPONSE_CODES, 
  CUSTOMER_STATUS_OPTIONS 
} from "@/constants";
import {
  numeric,
  required,
  maxLength,
  alphaNumeric,
  maxLengthNumber,
} from "@/validators/form-validators";
import { capitalize } from "@/filters";

export default {
  name: "AddCustomerForm",
  /**
   * --------- Components --------------
   */
  components: {
    CardWidget,
    DateRangePicker
  },
  /**
   * ---------- Data Attributes -----------
   */
  data() {
    return {
      isFormValid: false,
      isLoading: false,
      areStatesLoading: false,
      statusOptions: CUSTOMER_STATUS_OPTIONS,
    };
  },
  /**
   * ------------- Props -------------------
   */
  props: {
    customer: {
      type: Object,
      default: () => {
        return {
          name: "",
          customer_id: "",
          address_line_1: "",
          address_line_2: "",
          country: null,
          state: null,
          city: null,
          zipcode: null,
          status: "active",
          start_date: "",
          renewal_date: "",
        };
      },
    },
    subscription: {
      type: Object,
      default: () => {
        return {
            subscription_package_type: "",
          billing_subscription_id: "",
          billing_customer_id: "",
            placement_reports_number: null,
        };
      },
    },
    showCustomerInfo: {type: Boolean, default: true},
    showSubscription: {type: Boolean, default: true},

    successMessage: {
      type: String,
      default: ''
    },
    
  },
  /**
   * --------Computed properties---------
   */
  computed: {
    ...mapGetters({
      countries: "app/countries",
      states: "app/states",
      storeSnackbar: "ui/snackbar",
    }),
    
    /**
     * Snackbar types
     */
    snackbarTypes() {
      return this.$appConfig.snackbar.snackbarTypes;
    },
    /**
     * Get Customer Id
     */
    customerId(){
      return this.$route.params?.id
    },

    /**
     * get action type
     * @type {Boolean}
     */
    isEditCustomer(){
      return !!this.customerId
    }
  },
  /**
   * ---------Methods--------------
   */
  methods: {
    numeric,
    required,
    maxLength,
    alphaNumeric,
    maxLengthNumber,
    ...mapActions({
      getCountries: "app/getCountries",
      getStates: "app/getStates",
      setSnackbar: "ui/setSnackbar",
    }),
    /**
     * update isLoading attribute value
     */
    setIsLoading(val) {
      this.isLoading = val;
    },
    /**
     * Fetched states of a country
     * @param {String} country Country whose states are to be fetched
     */
    async getCountryStates(country) {
      this.areStatesLoading = true;
      await this.getStates(country);
      this.areStatesLoading = false;
    },
    /**
     * Save form data values into server
     */
    async submitCustomerForm() {
      try {
        if (!this.isFormValid) {
          return this.$refs.customerForm.validate();
        }
        this.setIsLoading(true);

        this.customer.subscription_package = this.subscription;
        if(this.isEditCustomer){
          const { status } = await updateCustomer(this.customerId, this.customer);
          if (isResponseCode(status, RESPONSE_CODES.success)) {
            this.setSnackbar({
              value: true,
              message: this.successMessage || this.$dictionary.app.customers.update.successMsg,
              type: this.snackbarTypes.success,
            });
          }
        } else {
          const { data, status } = await createCustomer(this.customer);
          // create account 
          await createAccount({
            name: this.customer.name,
            address_line_1 : this.customer.address_line_1,
            address_line_2 : this.customer.address_line_2,
            city: this.customer.city,
            country: this.customer.country,
            customer_id: data.id,
            state: this.customer.state
          });
          if (isResponseCode(status, RESPONSE_CODES.success)) {
            this.setSnackbar({
              value: true,
              message: this.$dictionary.app.customers.create.successMsg,
              type: this.snackbarTypes.success,
            });
            this.$router.push({ path: this.$paths.customers });
          }
        }
        
      } catch ({ message, response }) {
        let errorMessage = this.$errorMessage({ message });
        if (!isEmpty(response?.data)) {
          Object.keys(response?.data).forEach((prop) => {
            return (errorMessage = `${capitalize(prop)} ${
              response.data[prop]
            }`);
          });
        }
        !this.storeSnackbar.value &&
          this.setSnackbar({
            value: true,
            message: errorMessage,
            type: this.snackbarTypes.error,
          });
      } finally {
        this.setIsLoading(false);
      }
    },
  },
  /**
   * --------Created lifecycle hook -------------
   */
  async created() {
    this.setIsLoading(true);
    await this.getCountries();
    this.setIsLoading(false);
  },
};
</script>